// PARSE AND JUDGE A PRODUCT CSV, BEFORE ANY OF IT REACHES THE SERVER
// (slice import-b).
//
// Pure: no React, no network, no state. The modal renders what this returns
// and sends what this built; every decision about whether a row is importable
// is made here, so it is testable without a DOM and there is exactly one copy
// of it.
//
// ⚠️ WHY THE CLIENT VALIDATES AT ALL, when the backend validates anyway. The
// import is ALL OR NOTHING in one transaction: row 199 being wrong throws away
// the 198 good ones, and the caller is told only `products[198]: ...`. Sending
// a file with a knowably-bad row is therefore a guaranteed round trip that
// achieves nothing. This is not a substitute for the server's checks -- it is
// the same checks, early, so the preview can point at the row.
//
// ⚠️ IT IS STRICTER THAN buildPayload ALONE, and deliberately. buildPayload
// omits an empty brand (absent is absent, which is right for a hand-typed
// one-off where "unclassified" is a real state). For a BULK import a blank
// brand cell is a spreadsheet nobody finished, and an unclassified product is
// invisible to brand-lock evaluation -- so a locked brand silently stops
// firing on it. import_from_payload makes brand and category required for
// exactly this reason and only on this path; this file matches that.

import Papa from "papaparse";
// The draft type and its payload builder stay with the form that owns them,
// and are imported from there -- the same direction services/customer.test.ts
// already takes on CustomerPicker's draftToCreatePayload / EMPTY_DRAFT. There
// is one buildPayload, so the single-product create and every CSV row cannot
// disagree about what "absent" means.
import { buildPayload, EMPTY_DRAFT } from "@/components/NewProductModal";
import type { ProductDraft } from "@/components/NewProductModal";

/** The CSV header row, verbatim: the backend's own wire keys.
 *
 *  NO ALIAS MAP, DELIBERATELY. "Part No." and "OEM" are what a human would
 *  write, and accepting them would mean this file owns a second, drifting
 *  answer to what a column is called -- one that the single-product path and
 *  the server do not share. One spelling, stated in the template, and a
 *  missing one named out loud. */
export const IMPORT_COLUMNS = [
  "part_number", "brand", "category", "description", "manufacturer",
  "supplier_ref", "hsn_code", "default_origin", "uom", "weight", "volume",
] as const;

/** ⚠️ REQUIRED ON THIS PATH ONLY -- see the header comment. */
export const REQUIRED_COLUMNS = ["part_number", "brand", "category"] as const;

/** Mirrors `_MAX_IMPORT_PRODUCTS` in addons/anixify_cpq/models/product.py.
 *
 *  ⚠️ A REFUSAL, NEVER A TRUNCATION, AND NEVER AN AUTO-SPLIT. Trimming would
 *  import a subset while looking complete. Chunking into several batches would
 *  break the all-or-nothing guarantee that is the whole point of the endpoint:
 *  batch 1 commits, batch 2 fails, and someone is left working out which half
 *  of their file landed. Checked here as well as server-side so an over-cap
 *  file is caught before it is uploaded, with the same reason given. */
export const MAX_IMPORT_ROWS = 200;

/** One data row, in payload order. */
export interface CsvRow {
  /** 1-based position among DATA rows. Not the spreadsheet line number: with a
   *  header row present, spreadsheet line = rowNo + 1. */
  rowNo: number;
  /** 0-based position in the `products` array -- what `products[i]` in a
   *  backend error refers to. Equal to the array index, because a send is
   *  gated on every row being valid, so no row is ever dropped from the
   *  payload. If that gate is ever relaxed, this must stop being derived from
   *  position: a filtered payload would shift every backend error onto the
   *  wrong row, silently. */
  index: number;
  values: ProductDraft;
  /** Built only for a row with no blocking errors. */
  payload?: Record<string, unknown>;
  /** BLOCKING. Any of these anywhere in the file refuses the send. */
  errors: string[];
  /** ADVISORY. Rendered, never blocking -- duplicates are legitimate. */
  notices: string[];
}

export interface CsvImportResult {
  rows: CsvRow[];
  /** BLOCKING, and about the file rather than a row. */
  fileErrors: string[];
  /** ADVISORY, about the file: ignored columns, mostly. */
  fileNotices: string[];
  /** Headers present that the product master has no field for. Reported, not
   *  swallowed: a typo'd "descripton" silently drops a whole column. */
  unknownHeaders: string[];
  /** Required headers absent from the file. Blocking. */
  missingHeaders: string[];
}

/** ⚠️ COPIED KEY BY KEY, NOT SPREAD. `{...EMPTY_DRAFT, ...row}` looks
 *  equivalent and is not: papaparse gives `undefined` for a cell a short row
 *  never reached, and an `undefined` OVERWRITES the "" it spreads onto, so
 *  buildPayload's `draft[key].trim()` throws on the whole file. Only a real
 *  string is taken; anything else leaves the empty default standing. */
function toDraft(row: Record<string, unknown>): ProductDraft {
  const draft: ProductDraft = { ...EMPTY_DRAFT };
  for (const col of IMPORT_COLUMNS) {
    const v = row[col];
    if (typeof v === "string") draft[col] = v;
  }
  return draft;
}

/** "did you mean CommScope?" for a case-only miss.
 *
 *  ⚠️ SUGGESTS, NEVER CORRECTS. The backend resolves a taxonomy name with '='
 *  and not 'ilike' -- test_a_CASE_MISMATCHED_brand_is_refused pins that -- so
 *  "commscope" really is refused. Silently fixing it would make this client
 *  the authority on master data it does not own, and would quietly accept a
 *  file the single-product form rejects. */
function caseOnlyMatch(value: string, options: string[]): string | undefined {
  const lowered = value.toLowerCase();
  return options.find((o) => o.toLowerCase() === lowered);
}

function taxonomyError(
  field: "brand" | "category",
  value: string,
  options: string[],
): string | undefined {
  if (options.includes(value)) return undefined;
  const suggestion = caseOnlyMatch(value, options);
  if (suggestion) {
    return `${field} "${value}" is not an exact match — did you mean "${suggestion}"? `
         + `Brand and category are matched exactly, including case.`;
  }
  return `${field} "${value}" is not one of the active ${field} names. `
       + `Allowed: ${options.join(", ") || "(none)"}.`;
}

/** Parse and validate a CSV file's text against the live taxonomy.
 *
 *  `brandOptions` / `categoryOptions` are the INTERSECTED lists (see
 *  services/productTaxonomy.ts) -- the server's active names narrowed to the
 *  ones this build can evaluate a brand lock against. Passing the raw taxonomy
 *  would accept a backend-only brand, which is the silent failure that
 *  intersect exists to prevent. */
export function parseProductCsv(
  text: string,
  brandOptions: string[],
  categoryOptions: string[],
): CsvImportResult {
  const fileErrors: string[] = [];
  const fileNotices: string[] = [];

  // A UTF-8 BOM is what Excel writes, and it lands on the FIRST header --
  // "﻿part_number" matches nothing, so every required column reads as
  // missing and the file is refused for a character nobody can see.
  const cleaned = text.replace(/^﻿/, "");

  const parsed = Papa.parse<Record<string, unknown>>(cleaned, {
    header: true,
    skipEmptyLines: "greedy",
    // Whitespace only. NOT an alias map: "part_number " and "part_number"
    // differ by something invisible, and refusing that would be a puzzle
    // rather than a message.
    transformHeader: (h) => h.trim(),
  });

  const headers = (parsed.meta.fields ?? []).filter((h) => h !== "");
  const known = new Set<string>(IMPORT_COLUMNS);
  const unknownHeaders = headers.filter((h) => !known.has(h));
  const missingHeaders = REQUIRED_COLUMNS.filter((c) => !headers.includes(c));

  if (headers.length === 0) {
    fileErrors.push("This file has no header row. The first line must name the columns.");
  } else if (missingHeaders.length > 0) {
    // `else`: with no headers at all, every column is "missing", and saying so
    // as well would bury the one fact that explains it.
    fileErrors.push(
      `Missing required column${missingHeaders.length === 1 ? "" : "s"}: `
      + `${missingHeaders.join(", ")}. Column names must match exactly — `
      + `download the template to see them.`,
    );
  }

  // ⚠️ SAID, NOT SWALLOWED, and not blocking either. The backend's allowlist
  // drops a key it does not know, so the row imports fine -- but a "price"
  // column that vanishes without a word reads as a price that was stored, and
  // a typo'd "descripton" loses a whole column silently.
  if (unknownHeaders.length > 0) {
    fileNotices.push(
      `Ignored column${unknownHeaders.length === 1 ? "" : "s"}: `
      + `${unknownHeaders.join(", ")}. The product master has no such field, `
      + `so ${unknownHeaders.length === 1 ? "it" : "they"} will not be imported.`,
    );
  }

  // NO DELIMITER CHECK HERE, and that is not an omission. Papaparse detects
  // the separator itself and handles comma, TAB, semicolon and pipe, so a
  // tab-separated export saved under a .csv name parses correctly rather than
  // failing -- there is no error to report. Its own UndetectableDelimiter
  // warning fires on an empty file and on a legitimately single-column one,
  // neither of which is a delimiter problem, so surfacing that would bury the
  // message that actually explains the file.

  const data = parsed.data ?? [];

  if (data.length === 0 && fileErrors.length === 0) {
    // Refused rather than treated as a no-op, matching import_from_payload:
    // a file that never loaded must not read as a successful import.
    fileErrors.push("This file has no data rows.");
  }

  if (data.length > MAX_IMPORT_ROWS) {
    fileErrors.push(
      `This file has ${data.length} rows and at most ${MAX_IMPORT_ROWS} can be imported `
      + `at once. Split it — the file is deliberately not trimmed, because a `
      + `trimmed batch would import a subset while looking complete.`,
    );
  }

  const rows: CsvRow[] = data.map((raw, index) => {
    const values = toDraft(raw);
    const errors: string[] = [];
    const notices: string[] = [];

    // A row carrying MORE fields than there are headers. Papaparse parks the
    // surplus here, and it almost always means an unquoted comma inside a
    // description -- in which case every value after it is in the wrong
    // column, so the row must not be trusted rather than merely trimmed.
    if (Array.isArray(raw.__parsed_extra)) {
      errors.push(
        "This row has more values than there are columns — most often an "
        + 'unquoted comma inside a description. Wrap the cell in "quotes".',
      );
    }

    // Required, and judged AFTER trimming: "   " is truthy, and a product
    // whose identity is whitespace is blank in every table and unfindable by
    // the search that matches on that column. The server strips and judges the
    // same way.
    for (const col of REQUIRED_COLUMNS) {
      if (values[col].trim() === "") {
        errors.push(`${col} is required and this row leaves it blank.`);
      }
    }

    const brand = values.brand.trim();
    if (brand !== "") {
      const err = taxonomyError("brand", brand, brandOptions);
      if (err) errors.push(err);
    }
    const category = values.category.trim();
    if (category !== "") {
      const err = taxonomyError("category", category, categoryOptions);
      if (err) errors.push(err);
    }

    // Not blocking: buildPayload OMITS an unparseable measurement rather than
    // sending a 0, so the row imports with the field absent. But absent and
    // "someone typed 12kg into a number column" look identical afterwards, so
    // it is worth saying before the import rather than after.
    for (const col of ["weight", "volume"] as const) {
      const rawValue = values[col].trim();
      if (rawValue !== "" && !Number.isFinite(Number(rawValue))) {
        notices.push(
          `${col} "${rawValue}" is not a number, so it will be left empty rather than guessed at.`,
        );
      }
    }

    const row: CsvRow = { rowNo: index + 1, index, values, errors, notices };
    if (errors.length === 0) row.payload = buildPayload(values);
    return row;
  });

  // ---- Duplicates WITHIN the file -----------------------------------------
  //
  // ADVISORY, and it must stay that way. Two rows sharing a part number
  // legitimately make two products -- part numbers are not unique across
  // manufacturers and the backend has no constraint saying they are
  // (test_two_rows_sharing_a_part_number_make_two_products pins it). This is
  // still worth saying, because the far likelier cause is a copy-paste.
  const seen = new Map<string, number[]>();
  for (const row of rows) {
    const partNo = row.values.part_number.trim();
    if (partNo === "") continue;
    const at = seen.get(partNo);
    if (at) at.push(row.rowNo);
    else seen.set(partNo, [row.rowNo]);
  }
  for (const [partNo, rowNos] of seen) {
    if (rowNos.length < 2) continue;
    for (const row of rows) {
      if (row.values.part_number.trim() !== partNo) continue;
      row.notices.push(
        `"${partNo}" appears ${rowNos.length} times in this file (rows ${rowNos.join(", ")}). `
        + `Each will be created as a separate product.`,
      );
    }
  }

  return { rows, fileErrors, fileNotices, unknownHeaders, missingHeaders };
}

/** Whether the file may be sent at all. */
export function hasBlockingProblem(result: CsvImportResult): boolean {
  return result.fileErrors.length > 0
    || result.rows.length === 0
    || result.rows.some((r) => r.errors.length > 0);
}

/** The `products` array, in row order.
 *
 *  ⚠️ EVERY ROW, NOT THE VALID ONES. A send is gated on hasBlockingProblem
 *  being false, so by here every row has a payload. Filtering instead would
 *  shift each row's position, and the backend answers POSITIONALLY --
 *  `products[3]` would then name a different row than the one that failed,
 *  which is the silent kind of wrong. Throws rather than skipping if that
 *  invariant is ever broken. */
export function importSpecs(result: CsvImportResult): Record<string, unknown>[] {
  return result.rows.map((row) => {
    if (!row.payload) {
      throw new Error(
        `Row ${row.rowNo} has no payload and must not be sent. `
        + `Check hasBlockingProblem() before calling importSpecs().`,
      );
    }
    return row.payload;
  });
}

/** Pull the row index out of a backend batch error.
 *
 *  ⚠️ TWO FORMS, BOTH REAL, and they come from different raise sites in
 *  import_from_payload:
 *
 *      "products[3]: brand and category are required."   (colon)
 *      "products[3] must be an object, got str."         (no colon)
 *
 *  Matching only the colon form would drop the index on the shape error and
 *  the message would point at no row at all. An unrecognised message is
 *  returned whole with no index rather than being reshaped -- an error nobody
 *  can attribute is still an error worth showing verbatim. */
export function parseImportError(message: string): { rowIndex?: number; message: string } {
  const match = /^products\[(\d+)\](?::\s*|\s+)([\s\S]*)$/.exec(message.trim());
  if (!match) return { message };
  return { rowIndex: Number(match[1]), message: match[2] };
}

/** Rows whose part number ALREADY EXISTS in the catalog.
 *
 *  ⚠️ ADVISORY, NEVER BLOCKING, and this is the decision import-a carried
 *  forward. The endpoint is create-only and does not dedupe, so re-importing a
 *  file really does double the catalog -- but a part number is legitimately
 *  not unique, so refusing one would refuse a catalog that is simply true. The
 *  warning lives here, loudly, and the choice stays with the person.
 *
 *  Exact match, like NewProductModal's own check: the products endpoint is
 *  `ilike` over part_number OR description, so a substring hit is not a
 *  duplicate, and warning on one would train people to ignore the warning that
 *  matters. */
export function catalogDuplicateRowNos(
  result: CsvImportResult,
  existingPartNos: Set<string>,
): number[] {
  return result.rows
    .filter((r) => existingPartNos.has(r.values.part_number.trim()))
    .map((r) => r.rowNo);
}

/** The template file's contents: the header row, and nothing else.
 *
 *  One example row was considered and left out -- it would be imported by
 *  anyone who filled in the rest and forgot to delete it, creating a product
 *  called EXAMPLE-1. */
export function templateCsv(): string {
  return `${IMPORT_COLUMNS.join(",")}\n`;
}
